import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

function getPasswordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)            score++;
  if (pw.length >= 12)           score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  return score;
}

const STRENGTH_LABEL = ['', 'Weak', 'Fair', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLOR = ['', '#c0392b', '#c4722a', '#c4722a', '#2a7a6f', '#1e5c54'];

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M1 9s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M1 9s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2 2l14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

export default function ResetPasswordScreen({ onSuccess }) {
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPassword, setShowPwd]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [done, setDone]               = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched]         = useState({});

  const strength = getPasswordStrength(password);

  function handleBlur(field) {
    setTouched(t => ({ ...t, [field]: true }));
    if (field === 'password' && password.length < 6)
      setFieldErrors(fe => ({ ...fe, password: 'At least 6 characters required' }));
    if (field === 'confirm' && password !== confirm)
      setFieldErrors(fe => ({ ...fe, confirm: "Passwords don't match" }));
  }

  function handlePasswordChange(val) {
    setPassword(val);
    if (touched.password)
      setFieldErrors(fe => ({ ...fe, password: val.length < 6 ? 'At least 6 characters required' : null }));
    if (touched.confirm)
      setFieldErrors(fe => ({ ...fe, confirm: val !== confirm ? "Passwords don't match" : null }));
  }

  function handleConfirmChange(val) {
    setConfirm(val);
    if (touched.confirm)
      setFieldErrors(fe => ({ ...fe, confirm: val !== password ? "Passwords don't match" : null }));
  }

  async function handleSubmit() {
    setTouched({ password: true, confirm: true });
    const pwErr      = password.length < 6 ? 'At least 6 characters required' : null;
    const confirmErr = password !== confirm ? "Passwords don't match" : null;
    setFieldErrors({ password: pwErr, confirm: confirmErr });
    if (pwErr || confirmErr) return;

    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div style={s.screen}>
        <div style={s.card}>
          <div style={s.wordmark}>
            <span style={s.wordmarkBold}>Clear</span>
            <span style={s.wordmarkLight}>path</span>
          </div>
          <div style={s.sentIcon}>✅</div>
          <div style={s.sentTitle}>Password updated</div>
          <p style={s.sentText}>Your password has been changed. You're all set.</p>
          <button style={s.primaryBtn} onClick={onSuccess}>Continue to ClearPath</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.screen}>
      <div style={s.card}>
        <div style={s.wordmark}>
          <span style={s.wordmarkBold}>Clear</span>
          <span style={s.wordmarkLight}>path</span>
        </div>

        <div style={s.heading}>Set new password</div>
        <p style={s.subheading}>Choose a new password for your ClearPath account.</p>

        {error && <div style={s.errorBox}>{error}</div>}

        <div style={s.fieldGroup}>
          <div style={s.fieldLabel}>New password</div>
          <div style={s.passwordWrap}>
            <input
              style={{ ...s.input, ...s.passwordInput, ...(fieldErrors.password && touched.password ? s.inputError : {}) }}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => handlePasswordChange(e.target.value)}
              onBlur={() => handleBlur('password')}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              autoFocus
            />
            <button style={s.eyeBtn} onClick={() => setShowPwd(v => !v)} tabIndex={-1} type="button">
              <EyeIcon visible={showPassword} />
            </button>
          </div>
          {fieldErrors.password && touched.password && <div style={s.fieldError}>{fieldErrors.password}</div>}

          {password.length > 0 && (
            <div style={s.strengthWrap}>
              <div style={s.strengthBars}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} style={{ ...s.strengthBar, background: i <= strength ? STRENGTH_COLOR[strength] : 'var(--border)' }} />
                ))}
              </div>
              <div style={{ ...s.strengthLabel, color: STRENGTH_COLOR[strength] }}>
                {STRENGTH_LABEL[strength]}
              </div>
            </div>
          )}
        </div>

        <div style={s.fieldGroup}>
          <div style={s.fieldLabel}>Confirm new password</div>
          <div style={s.passwordWrap}>
            <input
              style={{ ...s.input, ...s.passwordInput, ...(fieldErrors.confirm && touched.confirm ? s.inputError : {}) }}
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={e => handleConfirmChange(e.target.value)}
              onBlur={() => handleBlur('confirm')}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            <button style={s.eyeBtn} onClick={() => setShowConfirm(v => !v)} tabIndex={-1} type="button">
              <EyeIcon visible={showConfirm} />
            </button>
          </div>
          {fieldErrors.confirm && touched.confirm && <div style={s.fieldError}>{fieldErrors.confirm}</div>}
        </div>

        <button
          style={{ ...s.primaryBtn, opacity: loading || !password || !confirm ? 0.6 : 1 }}
          onClick={handleSubmit}
          disabled={loading || !password || !confirm}
        >
          {loading ? 'Saving…' : 'Set new password'}
        </button>
      </div>
    </div>
  );
}

const s = {
  screen:        { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: '24px' },
  card:          { width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '14px' },
  wordmark:      { display: 'flex', alignItems: 'baseline', gap: '2px', marginBottom: '8px' },
  wordmarkBold:  { fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '22px', color: 'var(--teal)', letterSpacing: '-0.5px' },
  wordmarkLight: { fontFamily: 'var(--font-display)', fontWeight: 300, fontStyle: 'italic', fontSize: '22px', color: 'var(--ink-2)', letterSpacing: '-0.3px' },
  heading:       { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px' },
  subheading:    { fontSize: '13.5px', color: 'var(--ink-3)', lineHeight: 1.5, margin: 0 },
  errorBox:      { padding: '12px 16px', background: '#fef0f0', border: '1px solid #f5c6c6', borderRadius: 'var(--radius-md)', fontSize: '13px', color: '#c0392b' },
  fieldGroup:    { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel:    { fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' },
  fieldError:    { fontSize: '12px', color: '#c0392b', fontWeight: 500 },
  input:         { width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--surface)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  inputError:    { borderColor: '#e88080' },
  passwordWrap:  { position: 'relative' },
  passwordInput: { paddingRight: '44px' },
  eyeBtn:        { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: '4px', display: 'flex', alignItems: 'center' },
  strengthWrap:  { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' },
  strengthBars:  { display: 'flex', gap: '3px', flex: 1 },
  strengthBar:   { flex: 1, height: '3px', borderRadius: '2px', transition: 'background 0.2s' },
  strengthLabel: { fontSize: '11px', fontWeight: 600, width: '36px', textAlign: 'right', flexShrink: 0 },
  primaryBtn:    { width: '100%', padding: '15px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--teal)', color: 'white', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
  sentIcon:      { fontSize: '40px', textAlign: 'center' },
  sentTitle:     { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '22px', color: 'var(--ink)', textAlign: 'center' },
  sentText:      { fontSize: '13.5px', color: 'var(--ink-2)', lineHeight: 1.6, textAlign: 'center', margin: 0 },
};
